<template>
  <div class="header-navigation">
    <span
      id="navigation-title"
      class="visually-hidden"
      v-text="$string('general.site_header.navigation')"
    />

    <nav class="header-navigation__nav">
      <ul class="header-navigation__list">
        <template v-for="(tier1, index) of navigation.items">
          <li
            v-if="tier1.title !== 'hide-me'"
            :key="tier1.handle"
            class="header-navigation__item"
            :class="{ [$class('active')]: index === activeIndex }"
            @mouseover="showMeganav(index)"
            @mouseleave="hideMeganav"
          >
            <btn
              v-if="blocks[index]"
              :class="linkLabelCssClass(tier1.sale)"
              modifiers="block"
              :label="tier1.title"
              :url="tier1.url"
              class-label="header-navigation__link-label text-body-m-bold"
              :show-icon="false"
            >
              <template #icon>
                <icon-chevron-down />
              </template>
            </btn>

            <btn
              v-else
              :class="linkLabelCssClass(tier1.sale)"
              modifiers="block"
              :label="tier1.title"
              :url="tier1.url"
              class-label="header-navigation__link-label text-body-m-bold"
              :show-icon="false"
            />

            <div
              v-if="blocks[index]"
              ref="scrollElement"
              class="header-navigation__meganav"

              :class="{
                'header-navigation__meganav--menu-tab': blocks[index].type === 'menu_tab',
              }"
              :style="{
                top: `${headerHeight}px`,
                maxHeight: calculateMaxHeight(),
              }"
              @mouseenter="activeIndex = index"
            >
              <div class="header-navigation__meganav-inner">
                <template v-if="blocks[index].type === 'menu_tab'">
                  <div
                    v-if="blocks[index].menus.length"
                    class="header-navigation__meganav-linklist"
                    :class="{
                      'header-navigation__meganav-linklist--tertiary':
                        blocks[index].menus.length === 3,
                    }"
                  >
                    <template v-for="menu of blocks[index].menus">
                      <div
                        v-if="menu.items.length"
                        :key="menu.handle"
                        class="header-navigation__linklist"
                      >
                        <p
                          class="
                            header-navigation__linklist-title
                            text-heading-xs
                            text-heading-3xs-desktop
                          "
                          v-text="menu.title"
                        />

                        <ul>
                          <template v-for="item of menu.items">
                            <li
                              v-if="item.title !== 'hide-me'"
                              :key="item.handle"
                              class="
                                header-navigation__linklist-item
                                text-heading-m
                              "
                            >
                              <menu-item
                                :item="item"
                                :liquid="liquid"
                              />
                            </li>
                          </template>
                        </ul>
                      </div>
                    </template>
                  </div>
                </template>

                <div v-else-if="blocks[index].type === 'product_tab'">
                  <a
                    :href="tier1.url"
                    class="
                      header-navigation__linklist-title
                      text-body-l-regular
                      text-link-hover
                    "
                    v-text="blocks[index].settings.products_heading.length ?
                      blocks[index].settings.products_heading : tier1.title
                    "
                  />

                  <navigation-product
                    v-if="blocks[index].products.length"
                    :products="blocks[index].products"
                    :liquid="liquid"
                  />
                </div>

                <div
                  class="header-navigation__banner-wrapper"
                  :class="{
                    'header-navigation__banner-wrapper--menu-tab':
                      blocks[index].type === 'menu_tab',
                  }"
                >
                  <navigation-banner
                    :block="blocks[index]"
                  />
                </div>

                <btn
                  v-if="blocks[index].settings.cta_url_3"
                  class="header-navigation__cta-btn"
                  :class="{
                    'header-navigation__cta-btn--full-width':
                      blocks[index].settings.layout === 'full_width'
                  }"
                  modifiers="block center"
                  :label="blocks[index].settings.cta_text_3"
                  :url="blocks[index].settings.cta_url_3"
                  class-label="text-other-button-s"
                  hover-only
                >
                  <template #hoverIcon>
                    <icon-arrow-right />
                  </template>
                </btn>
              </div>
            </div>
          </li>
        </template>
      </ul>
    </nav>
  </div>
</template>

<script>

/**
 * Vue: Header navigation (header-navigation)
 * -----------------------------------------------------------------------------
 * Desktop navigation.
 *
 */
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'

import MenuItem from '~async/menu-item/menu-item'
import NavigationBanner from '~async/navigation-banner/navigation-banner'
import NavigationProduct from '~async/navigation-product/navigation-product'

import Btn from '~global/btn/btn'

import IconArrowRight from '~icons/directional-navigation/arrow/right.svg'
import IconChevronDown from '~icons/directional-navigation/chevron/down.svg'

import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'HeaderNavigation',

  components: {
    Btn,
    IconArrowRight,
    IconChevronDown,
    MenuItem,
    NavigationBanner,
    NavigationProduct,
  },

  props: {
    navigation: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
    liquid: {
      type: Object,
      default: () => ({}),
    },
    blocks: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      activeIndex: false,
    }
  },

  computed: {

    ...mapGetters({
      headerHeight: 'site-header/getHeight',
    }),
  },

  watch: {
    activeIndex: {
      handler(newIndex, oldIndex) {
        this.setScroll(newIndex, oldIndex)
      },
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
    }),

    /**
     * Set the max height of the banner
     */
    calculateMaxHeight() {
      if (window.innerHeight > 850) {
        return '850px'
      }

      return `calc(100vh - ${this.headerHeight}px)`
    },

    /**
     * Disable/enable scroll based on active navigation item.
     * @param {Number} newIndex - Index of active navigation item.
     * @param {Number} oldIndex - Index of previous active navigation item.
     */
    setScroll(newIndex, oldIndex) {
      if (this.activeIndex === false) {
        enableBodyScroll(this.$refs.scrollElement[oldIndex])
        this.closeOverlay('quicksearch')

        return
      }

      disableBodyScroll(this.$refs.scrollElement[newIndex])
      this.closeOverlay('quicksearch')
    },

    /**
     * Show meganav.
     * @param {Number} index - Index of active navigation item.
     */
    showMeganav(index) {
      if (!this.blocks[index]) {
        return
      }

      this.activeIndex = index
    },

    /**
     * Set link label css class.
     * @param {Boolean} isSale - Is sale link item.
     */
    linkLabelCssClass(isSale = false) {
      return `${isSale ? 'header-navigation__link-sale' : ''}
      header-navigation__link`
    },

    /**
     * Hide meganav.
     */
    hideMeganav() {
      this.activeIndex = false
    },
  },
}
</script>

<style lang="scss">
@import './header-navigation';
@import '~async/promo-banner/promo-banner';
</style>
